import { ConfirmationModal, CustomButton, CustomDrawer } from '@components';
import { ClientOrganization, CreditReason, OrganizationStatus } from '@graphql';
import AttachMoney from '@mui/icons-material/AttachMoney';
import AddCardOutlinedIcon from '@mui/icons-material/AddCardOutlined';
import {
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { useMemo, useState } from 'react';
import * as Yup from 'yup';
import { COLORS } from '@styles';
import { camel2Title, formatMoney } from '@utils';
import { OrganizationInfo } from '../OrganizationInfo';

interface IApplyCreditDrawerProps {
  open: boolean;
  onClose: () => void;
  organization: ClientOrganization;
}

const styles = {
  formWrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '85vh',
  } as React.CSSProperties,
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
    scrollbarWidth: 'none',
    padding: '0 1rem',
    gap: '1.5rem',
  } as React.CSSProperties,
  formItems: {
    borderRadius: '1rem',
  },
  inputBorder: {
    borderRadius: '1rem',
  },
  button: {
    padding: '1.5rem 1rem 0 1rem',
    borderTop: `1px solid ${COLORS.NEUTRAL_DARK_2}`,
    marginTop: 'auto',
  },
  organizationStatusContainer: {
    display: 'flex',
    flexDirection: 'column',
    color: COLORS.NEUTRAL_LIGHT_3,
    border: `1px solid ${COLORS.BORDER_DARK}`,
    borderRadius: '1rem',
  } as React.CSSProperties,
  organizationStatusItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem 1rem',
  },
  organizationStatus: {
    [OrganizationStatus.Active]: {
      color: COLORS.POSITIVE_NEUTRAL,
    },
    [OrganizationStatus.Inactive]: {
      color: COLORS.NEGATIVE_NEUTRAL,
    },
    [OrganizationStatus.Created]: {
      color: COLORS.NEUTRAL_LIGHT_0,
    },
  },
};

export function ApplyCreditDrawer({ open, onClose, organization }: IApplyCreditDrawerProps) {
  const [isOpenConfirmationModal, setIsOpenConfirmationModal] = useState(false);

  const handleCloseConfirmationModal = () => setIsOpenConfirmationModal(false);
  const handleOpenConfirmationModal = () => setIsOpenConfirmationModal(true);

  const initialValues = useMemo(
    () => ({
      id: '',
      creditAmount: 0,
      creditReason: CreditReason.ServiceInterruption,
      description: '',
    }),
    [],
  );

  const validationSchema = Yup.object().shape({
    creditAmount: Yup.number().required('Credit amount is required.'),
    creditReason: Yup.string().required('Credit reason is required.'),
    description: Yup.string().when(['creditReason'], {
      is: (creditReason: CreditReason) => creditReason === CreditReason.Other,
      then: (schema: Yup.StringSchema) => schema.required('Description is required'),
      otherwise: (schema: Yup.StringSchema) => schema,
    }),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: () => {
      handleOpenConfirmationModal();
    },
  });

  const creditReasonSelectOptions = useMemo(
    () =>
      Object.values(CreditReason).map((reason) => ({ label: camel2Title(reason), value: reason })),
    [],
  );

  const mockCurrentAccountBalance = 0;

  // TODO: Add API call to apply credit
  const onConfirm = async () => {
    handleCloseConfirmationModal();
    onClose();
  };

  return (
    <CustomDrawer
      open={open}
      onClose={onClose}
      headerLabel="Apply Credit"
      headerIcon={<AddCardOutlinedIcon />}
    >
      <form style={styles.formWrapper} onSubmit={formik.handleSubmit}>
        <div style={styles.formContainer}>
          <OrganizationInfo organization={organization} />
          <div style={styles.organizationStatusContainer}>
            <div style={styles.organizationStatusItem}>
              <Typography variant="body1" fontSize="0.875rem">
                Organization Status
              </Typography>
              <Typography
                variant="body1"
                sx={styles.organizationStatus[organization.status]}
                fontSize="0.875rem"
                fontWeight={600}
              >
                {organization.status}
              </Typography>
            </div>
            <div style={styles.organizationStatusItem}>
              <Typography variant="body1" fontSize="0.875rem">
                Current Account Balance
              </Typography>
              <Typography variant="body1" fontSize="0.875rem">{`${formatMoney(
                mockCurrentAccountBalance,
                2,
              )} CAD`}</Typography>
            </div>
          </div>
          <TextField
            sx={styles.formItems}
            fullWidth
            label="Credit Amount"
            name="creditAmount"
            onChange={formik.handleChange}
            value={formik.values.creditAmount}
            variant="outlined"
            error={!!formik.errors.creditAmount}
            helperText={
              formik.errors.creditAmount ||
              "This credit will be added to the customer's account balance and applied to future charges"
            }
            InputProps={{
              style: styles.inputBorder,
              startAdornment: (
                <InputAdornment position="start">
                  <AttachMoney />
                </InputAdornment>
              ),
            }}
          />
          <FormControl fullWidth>
            <InputLabel sx={styles.formItems}>Credit Reason</InputLabel>
            <Select
              sx={styles.formItems}
              fullWidth
              name="creditReason"
              onChange={formik.handleChange}
              value={formik.values.creditReason}
              label="Credit Reason"
            >
              {creditReasonSelectOptions.map(({ label, value }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            sx={styles.formItems}
            fullWidth
            label="Description"
            name="description"
            onChange={formik.handleChange}
            value={formik.values.description}
            variant="outlined"
            required={formik.values.creditReason === CreditReason.Other}
            multiline
            minRows={4}
            error={!!formik.errors.description}
            helperText={formik.errors.description}
            InputProps={{ style: styles.inputBorder }}
          />

          <div style={styles.button}>
            <CustomButton variant="default" type="submit" isLoading={formik.isSubmitting}>
              Apply Credit
            </CustomButton>
          </div>
        </div>
      </form>
      <ConfirmationModal
        icon={<AddCardOutlinedIcon />}
        title={`Apply ${formik.values.creditAmount} credit to ${organization.name}?`}
        onClose={handleCloseConfirmationModal}
        isOpen={isOpenConfirmationModal}
        variant="default"
        onConfirm={onConfirm}
        confirmationButtonLabel="Confirm Credit"
        confirmationText={organization.name || ''}
      >
        <p>
          This credit will be added to their account balance and automatically applied to future
          invoices.
        </p>
        <p>Type the organization name to confirm cancellation: </p>
        <strong>{organization.name}</strong>
      </ConfirmationModal>
    </CustomDrawer>
  );
}
